import React, { HTMLProps, useContext } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { useTranslation } from 'react-i18next';
import { usePostDialogueData } from '../../../../api/pension-robot/usePostPensionDialogueData';
import { RobotEnvironmentContext } from '../../../../contexts/general/RobotEnvironmentContext';
import { OtherIncome, PensionContext } from '../../../../contexts/pension-robot/PensionContext';
import { isMyBND } from '../../../../utils/functions';
import { reverseFormatNumber, toPrice } from '../../../../utils/parsers';
import { YesNoFormGroup } from '../../../general/form-inputs/YesNoFormGroup';

export const OtherIncomes: React.FC<{} & HTMLProps<HTMLDivElement>> = () => {
    const { t } = useTranslation('pension');
    const {
        parameters: { supplementInfo },
        actions,
    } = useContext(PensionContext);

    const updateToggle = (value: string | null) => {
        value === 'yes' && actions.setParameters({ supplementInfo: { otherIncomeAvailable: true } });
        value === 'no' && actions.setParameters({ supplementInfo: { otherIncomeAvailable: false } });
    };

    return (
        <div className="px-4 pb-8 pt-2 border-b">
            <YesNoFormGroup
                className="mb-6"
                title={{ label: t('pension-result-screen-other-income-text') }}
                value={!!supplementInfo?.otherIncomeAvailable ? 'yes' : 'no'}
                onChange={(value) => updateToggle(value)}
            />
            {!!supplementInfo?.otherIncomeAvailable && (
                <div className="flex flex-col gap-6">
                    <OtherIncomeInputField otherIncome="gift" />
                    <OtherIncomeInputField otherIncome="heritage" />
                    <OtherIncomeInputField otherIncome="surplusHouse" />
                    <OtherIncomeInputField otherIncome="other" />
                    <OtherIncomeTotal />
                </div>
            )}
        </div>
    );
};

const OtherIncomeInputField: React.FC<
    {
        otherIncome: OtherIncome;
    } & HTMLProps<HTMLDivElement>
> = ({ className, otherIncome }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { supplementInfo },
        actions,
    } = useContext(PensionContext);
    const { environment } = useContext(RobotEnvironmentContext);
    const usePostDialogueDataQuery = usePostDialogueData();

    const currentField =
        otherIncome === 'gift'
            ? supplementInfo?.otherIncome.gift
            : otherIncome === 'heritage'
            ? supplementInfo?.otherIncome.heritage
            : otherIncome === 'surplusHouse'
            ? supplementInfo?.otherIncome.surplusHouse
            : supplementInfo?.otherIncome.other;

    const updateField = (otherIncome: OtherIncome, value: string) => {
        actions.setParameters(
            otherIncome === 'gift'
                ? { supplementInfo: { otherIncome: { gift: reverseFormatNumber(value, 'nl-NL') * 100 } } }
                : otherIncome === 'heritage'
                ? { supplementInfo: { otherIncome: { heritage: reverseFormatNumber(value, 'nl-NL') * 100 } } }
                : otherIncome === 'surplusHouse'
                ? { supplementInfo: { otherIncome: { surplusHouse: reverseFormatNumber(value, 'nl-NL') * 100 } } }
                : { supplementInfo: { otherIncome: { other: reverseFormatNumber(value, 'nl-NL') * 100 } } },
        );
        isMyBND(environment) && usePostDialogueDataQuery.refetch();
    };

    const getLabelTitle = (otherIncome: OtherIncome) =>
        otherIncome === 'gift'
            ? t('pension-result-screen-other-income-gift')
            : otherIncome === 'heritage'
            ? t('pension-result-screen-other-income-heritage')
            : otherIncome === 'surplusHouse'
            ? t('pension-result-screen-other-income-surplus')
            : t('pension-result-screen-other-income-other');

    return (
        <div className={`${className} flex flex-row gap-2 sm:gap-4 flex-wrap sm:flex-nowrap items-center`}>
            <div className="w-36">
                <span>{getLabelTitle(otherIncome)}</span>
            </div>
            <div>
                <CurrencyInput
                    type="text"
                    placeholder="€"
                    className={
                        'bg-white text-black w-full py-2 px-4 border border-gray-200 rounded-lg focus:outline-hidden font-medium'
                    }
                    fixedDecimalLength={0}
                    decimalsLimit={2}
                    intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                    allowDecimals={false}
                    value={!!currentField ? Math.ceil(currentField / 100) : ''}
                    onValueChange={(value = '', _) => updateField(otherIncome, value)}
                />
            </div>
        </div>
    );
};

const OtherIncomeTotal: React.FC<{}> = () => {
    const { t } = useTranslation('pension');
    const {
        parameters: { supplementInfo },
    } = useContext(PensionContext);

    const calcTotalOtherIncome = () =>
        (supplementInfo?.otherIncome.gift ?? 0) +
        (supplementInfo?.otherIncome.heritage ?? 0) +
        (supplementInfo?.otherIncome.surplusHouse ?? 0) +
        (supplementInfo?.otherIncome.other ?? 0);

    return (
        <div className="flex flex-row gap-2 sm:gap-4 flex-wrap sm:flex-nowrap items-center">
            <div className="w-36">
                <span>{t('pension-result-screen-other-income-total')}</span>
            </div>
            <div className="ml-4">
                <span>
                    {toPrice(calcTotalOtherIncome(), {
                        onlyFullEuros: true,
                        withCurrencySymbol: true,
                    })}
                </span>
            </div>
        </div>
    );
};
